import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Ban, Check, Loader2, Ruler, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, inputCls } from "@/components/kit";
import { api, errMsg } from "@/lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 });

export function useJob(plan, kind, onPlan, okMsg, fetcher) {
  const stato = plan?.job?.[kind]?.stato;
  const was = useRef(stato);
  useEffect(() => {
    if (was.current === "in_corso" && stato === "ok") toast.success(okMsg);
    if (was.current === "in_corso" && stato === "errore") toast.error(plan.job[kind].errore);
    was.current = stato;
    if (stato !== "in_corso") return undefined;
    const t = setTimeout(async () => {
      const p = fetcher ? await fetcher() : (await api.get("/floorplans")).data.find((x) => x.id === plan.id);
      if (p) onPlan(p);
    }, 3000);
    return () => clearTimeout(t);
  }, [plan, stato]); // eslint-disable-line react-hooks/exhaustive-deps
  return stato === "in_corso";
}

function PickScale({ plan, picks, setPicks, onPlan }) {
  const [metri, setMetri] = useState("");
  const save = async () => {
    try {
      const [[x1, y1], [x2, y2]] = picks;
      onPlan((await api.post(`/floorplans/${plan.id}/scale`, { x1, y1, x2, y2, metri: String(metri).replace(",", ".") })).data);
      setPicks(null);
      toast.success("Scala salvata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-lg bg-[#FFF7ED] border border-[#F59E0B]/40 p-3 space-y-2 text-sm" data-testid="scale-pick">
      <p>{picks.length < 2 ? `Tocca sulla planimetria il ${picks.length ? "secondo" : "primo"} estremo di una misura che conosci.` : "Quanti metri ci sono tra i due punti?"}</p>
      {picks.length === 2 && <div className="flex gap-2"><input className={inputCls} value={metri} onChange={(e) => setMetri(e.target.value)} placeholder="es. 46,4" data-testid="scale-metri" /><Button size="sm" className="rounded-full" onClick={save} data-testid="scale-pick-save">Salva</Button></div>}
      <button className="text-xs underline text-[#7B929C]" onClick={() => setPicks(null)} data-testid="scale-pick-cancel">Annulla</button>
    </div>
  );
}

function AreaTools({ plan, onPlan, areaMode, setAreaMode }) {
  const a = plan.analisi;
  const reset = async () => onPlan((await api.post(`/floorplans/${plan.id}/area`, { reset: true })).data);
  if (areaMode) return <div className="rounded-lg bg-[#EAF5F7] p-3 text-sm flex items-center justify-between gap-2" data-testid="area-pick"><span>{areaMode === "add" ? "Tocca un'altra zona da aggiungere" : "Tocca la planimetria al centro della zona dove vanno i tavoli"}</span><button className="text-xs underline" onClick={() => setAreaMode(null)} data-testid="area-pick-cancel">Annulla</button></div>;
  return (
    <div className="space-y-2">
      {a?.mask && <p className="text-sm" data-testid="area-info">Area per i tavoli (in azzurro): <b>~{fmt(a.area_m2)} m²</b>{a.superficie_m2 ? ` • sulla planimetria ${fmt(a.superficie_m2)} m²` : ""}</p>}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAreaMode("set")} data-testid="area-set">{a?.mask ? "Rifai l'area toccando" : "Indica l'area toccando"}</Button>
        {a?.mask && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setAreaMode("add")} data-testid="area-add">+ Aggiungi zona</Button>}
        {a?.mask && <Button size="sm" variant="ghost" className="rounded-full text-[#991B1B]" onClick={reset} data-testid="area-reset">Togli</Button>}
      </div>
    </div>
  );
}

export function ScaleTools({ plan, onPlan, picks, setPicks, areaMode, setAreaMode }) {
  const [starting, setStarting] = useState(false);
  const running = useJob(plan, "analisi", onPlan, "Planimetria letta: controlla la scala");
  const busy = starting || running;
  const read = async () => {
    setStarting(true);
    try {
      onPlan((await api.post(`/floorplans/${plan.id}/analyze`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setStarting(false);
  };
  const confirm = async () => onPlan((await api.post(`/floorplans/${plan.id}/scale`, { confirm: true })).data);
  const s = plan.scala;
  const a = plan.analisi;
  return (
    <div className="rounded-xl border border-[#D4E6EC] bg-white p-4 space-y-3" data-testid="scale-tools">
      <div className="flex items-center gap-2 font-medium text-[#0D2B3A]"><Ruler size={16} /> Scala e area</div>
      {s ? (
        <div className="text-sm space-y-1" data-testid="scale-info">
          <p>Misura usata: <b>{s.testo || `${fmt(s.metri)} m`}</b> (linea arancione) {s.fonte === "manuale" ? "• indicata da te" : "• letta dall'IA"}</p>
          <p>Larghezza reale dell'immagine: <b>{fmt(plan.larghezza_m)} m</b></p>
          {!a?.mask && a?.area?.length > 2 && <p className="text-[#7B929C]">Area per i tavoli riconosciuta{a.superficie_m2 ? ` (~${fmt(a.superficie_m2)} m²)` : ""} • {a.ostacoli?.length || 0} ostacoli (in rosso)</p>}
          <p className={plan.scala_ok ? "text-[#15803D]" : "text-[#B45309]"} data-testid="scale-status">{plan.scala_ok ? "Scala confermata" : "Controlla che la linea arancione coincida con la quota scritta"}</p>
        </div>
      ) : <p className="text-sm text-[#7B929C]">L'IA legge le misure scritte sulla planimetria, il perimetro dell'area e gli alberi o gli ostacoli.</p>}
      {s && !picks && <AreaTools plan={plan} onPlan={onPlan} areaMode={areaMode} setAreaMode={setAreaMode} />}
      {picks ? <PickScale plan={plan} picks={picks} setPicks={setPicks} onPlan={onPlan} /> : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant={s ? "outline" : "default"} className="rounded-full" disabled={busy} onClick={read} data-testid="scale-ai-read">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}{busy ? "Leggo… (fino a 1 minuto)" : s ? "Rileggi con IA" : "Leggi la planimetria con IA"}</Button>
          {s && !plan.scala_ok && <Button size="sm" className="rounded-full bg-[#15803D] hover:bg-[#166534]" onClick={confirm} data-testid="scale-confirm"><Check size={14} className="mr-1" /> È corretta</Button>}
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setPicks([])} data-testid="scale-correct">Indica la scala con due punti</Button>
        </div>
      )}
    </div>
  );
}

export function BanTools({ plan, onPlan, draft, setDraft }) {
  const zones = plan.vietate || [];
  const save = async (list) => {
    try {
      onPlan((await api.put(`/floorplans/${plan.id}/vietate`, { zone: list })).data);
      setDraft(null);
      toast.success("Zone senza tavoli aggiornate");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-xl border border-[#D4E6EC] bg-white p-4 space-y-3" data-testid="ban-tools">
      <div className="flex items-center gap-2 font-medium text-[#0D2B3A]"><Ban size={16} className="text-[#B91C1C]" /> Zone senza tavoli</div>
      {draft ? (
        <div className="rounded-lg bg-[#FEF2F2] p-3 text-sm space-y-2" data-testid="ban-draft">
          <p>Tocca sulla planimetria gli angoli della zona da lasciare libera (palco, pista da ballo, buffet, passaggio…). Punti: <b>{draft.length}</b></p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B]" disabled={draft.length < 3} onClick={() => save([...zones, draft])} data-testid="ban-save">Salva zona</Button>
            {draft.length > 0 && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDraft(draft.slice(0, -1))} data-testid="ban-undo">Togli ultimo punto</Button>}
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setDraft(null)} data-testid="ban-cancel">Annulla</Button>
          </div>
        </div>
      ) : <Button size="sm" variant="outline" className="rounded-full border-[#B91C1C]/40 text-[#B91C1C]" onClick={() => setDraft([])} data-testid="ban-new">+ Disegna zona senza tavoli</Button>}
      {zones.length > 0 && (
        <ul className="space-y-1 text-sm">
          {zones.map((z, i) => <li key={i} className="flex items-center justify-between rounded-lg bg-[#FEF2F2] px-3 py-1.5" data-testid={`ban-item-${i}`}><span>Zona {i + 1} • {z.length} punti</span><button className="text-xs text-[#B91C1C] underline" onClick={() => save(zones.filter((_, j) => j !== i))} data-testid={`ban-del-${i}`}>Elimina</button></li>)}
        </ul>
      )}
      <p className="text-xs text-[#7B929C]">La disposizione automatica non mette tavoli in queste zone (in rosso sulla mappa).</p>
    </div>
  );
}

export function AutoPlace({ plan, onTables }) {
  const [f, setF] = useState({ n: 10, forma: "rotondo", diametro: 180, lunghezza: 240, larghezza: 100, posti: 10, keep_sposi: true, spazio: plan.spazio_m ?? 1.5, sedia_cm: Math.round((plan.sedia_m ?? 0.6) * 100) });
  const [busy, setBusy] = useState(false);
  const inp = (k) => <input type="number" className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`auto-${k}`} />;
  const run = async () => {
    if (plan.tables?.some((t) => !t.sposi) && !window.confirm("I tavoli già disegnati (esclusa la postazione sposi) verranno sostituiti. Continuare?")) return;
    setBusy(true);
    try {
      const r = (await api.post(`/floorplans/${plan.id}/autoplace`, { ...f, spazio: Number(String(f.spazio).replace(",", ".")), sedia: Number(f.sedia_cm) / 100 })).data;
      onTables(r.tables);
      (r.placed === r.requested ? toast.success : toast.warning)(`${r.message}. Ora numerali e salva.`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-[#D4E6EC] bg-white p-4 space-y-3" data-testid="auto-place">
      <div className="flex items-center gap-2 font-medium text-[#0D2B3A]"><Wand2 size={16} /> Disponi i tavoli in automatico</div>
      {!plan.scala_ok && <p className="text-xs text-[#B45309]">Conferma prima la scala, così i tavoli sono nella misura giusta.</p>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Quanti tavoli">{inp("n")}</Field>
        <Field label="Forma"><select className={inputCls} value={f.forma} onChange={(e) => setF({ ...f, forma: e.target.value })} data-testid="auto-forma"><option value="rotondo">Rotondo</option><option value="rettangolare">Rettangolare</option><option value="ovale">Ovale</option><option value="quadrato">Quadrato</option></select></Field>
        {f.forma === "rotondo" ? <Field label="Diametro (cm)">{inp("diametro")}</Field> : <><Field label={f.forma === "quadrato" ? "Lato (cm)" : "Lunghezza (cm)"}>{inp("lunghezza")}</Field>{f.forma !== "quadrato" && <Field label="Larghezza (cm)">{inp("larghezza")}</Field>}</>}
        <Field label="Posti per tavolo">{inp("posti")}</Field>
        <Field label="Spazio tra i tavoli (m)"><input type="number" step="0.1" min="0.3" max="5" className={inputCls} value={f.spazio} onChange={(e) => setF({ ...f, spazio: e.target.value })} data-testid="auto-spazio" /></Field>
        <Field label="Seduta sedia (cm)"><input type="number" step="5" min="0" max="150" className={inputCls} value={f.sedia_cm} onChange={(e) => setF({ ...f, sedia_cm: e.target.value })} data-testid="auto-sedia" /></Field>
      </div>
      <p className="text-xs text-[#7B929C]" data-testid="auto-spacing-hint">Ogni tavolo occupa la sua misura più {f.sedia_cm || 0} cm di sedia tutto intorno; tra lo schienale di una sedia e quello del tavolo vicino restano {String(f.spazio).replace(".", ",")} m di passaggio. Evita alberi e ostacoli. Le misure restano salvate per questa sala.</p>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C9A96A]" checked={f.keep_sposi} onChange={(e) => setF({ ...f, keep_sposi: e.target.checked })} data-testid="auto-keep-sposi" /> Tieni la postazione sposi dove l'ho messa</label>
      <Button className="rounded-full w-full" disabled={busy} onClick={run} data-testid="auto-run">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wand2 size={14} className="mr-1" />} Disponi i tavoli</Button>
    </div>
  );
}
